




























































import {
  defineComponent,
  nextTick,
  ref,
  computed,
  PropType,
  watch,
  onMounted
} from "@vue/composition-api";
import { ThreadFilterValue } from "./types";
import OccSelectbox from "@/components/Atoms/OccSelectbox/index.vue";
import { SelectItem } from "@/components/Atoms/OccSelectbox/types";
import SearchTextField from "@/components/Atoms/Forms/SearchTextField/index.vue";
import ToggleCheckboxWithLabel from "@/components/Pages/Scout/Candidates/SearchOption/ToggleCheckboxWithLabel/index.vue";
import ThreadUser from "@/components/Pages/Scout/Messages/ThreadUserList/ThreadUser/index.vue";
import { MessageThreadUser } from "@/components/Pages/Scout/Messages/ThreadUserList/ThreadUser/types";
import PageHeader from "@/components/Molecules/PageHeader/index.vue";
import OccButton from "@/components/Atoms/OccButton/index.vue";
import InfiniteLoading, { StateChanger } from "vue-infinite-loading";
import {
  convertSearchThreadsResponseItemToMessageThreadUser,
  convertThreadFilterValueToThreadFilter,
  fetchMessageThreads
} from "@/components/Pages/Scout/Messages/ThreadUserList/methods";
import { getImageDataUrl } from "@/grpc-web/scout/user_profile_image";
import DefaultUserImage from "@/assets/images/Pages/Scout/Candidates/DefaultUserImage.svg";
import { SHA256 } from "crypto-js";
import { occWhite } from "@/assets/styles/vars-for-script.scss";
import { MessageThreadsLastComment } from "@/components/Pages/Scout/Messages/ThreadUserList/ThreadUser/types";
import { useRouter } from "@/router";

export default defineComponent({
  name: "ThreadUserList",
  components: {
    OccSelectbox,
    SearchTextField,
    ToggleCheckboxWithLabel,
    ThreadUser,
    InfiniteLoading,
    PageHeader,
    OccButton
  },
  emits: ["select-message-thread-user"],
  props: {
    threadUserDeletedAt: {
      type: Number as PropType<number | undefined>,
      default: undefined
    },
    readThreadId: {
      type: Number as PropType<number | undefined>,
      default: undefined
    },
    updateThreadUser: {
      type: Object as PropType<MessageThreadsLastComment | undefined>,
      default: undefined
    }
  },
  setup(props, { emit }) {
    const filterSelectList: SelectItem[] = [
      {
        label: "すべて",
        value: ThreadFilterValue.FILTER_ALL
      },
      {
        label: "受諾済",
        value: ThreadFilterValue.FILTER_USER_ACCEPTED
      },
      {
        label: "受諾待ち",
        value: ThreadFilterValue.FILTER_USER_UNANSWERED
      },
      {
        label: "却下済",
        value: ThreadFilterValue.FILTER_USER_DECLINED
      }
    ];

    const router = useRouter();

    const currentFilter = ref<ThreadFilterValue>(ThreadFilterValue.FILTER_ALL);
    const nameOrUniqueIdFilter = ref<string>("");
    const unansweredOnlyFilter = ref<boolean>(false);
    const messageThreadUsers = ref<MessageThreadUser[]>([]);
    const page = ref<number>(1);
    const per = 15;
    const isLoadingMessageThreadUsers = ref<boolean>(false);
    const lastSearchParam = ref<string>("");

    onMounted(async () => {
      const savedData = localStorage.getItem("message:searchFilter");
      if (savedData) {
        const data = JSON.parse(savedData);
        if (data.nameOrUniqueId) {
          nameOrUniqueIdFilter.value = data.nameOrUniqueId;
        }
        if (data.selection) {
          currentFilter.value = data.selection as ThreadFilterValue;
        }
      }
      resetInfiniteLoading();
    });

    watch(
      () => props.threadUserDeletedAt,
      async () => {
        if (props.threadUserDeletedAt === undefined) {
          return;
        }
        // メッセージスレッドのユーザーが削除されたら再検索してリストを更新する
        resetInfiniteLoading();
      }
    );

    watch(
      () => props.readThreadId,
      () => {
        if (!props.readThreadId) {
          return;
        }
        // 未読バッジを消去
        setReadCommentCount(props.readThreadId);
      }
    );

    watch(
      () => props.updateThreadUser,
      () => {
        if (!props.updateThreadUser) {
          return;
        }
        // 現在スレッドの最後のメッセージを更新
        updateLastMessage(props.updateThreadUser);
      },
      { immediate: true }
    );

    // 最後に検索したパラメータとの比較がしやすいよう、Hashを利用する
    const currentSearchParamHash = computed<string>(() => {
      return generateSearchParamHash(
        currentFilter.value,
        nameOrUniqueIdFilter.value,
        unansweredOnlyFilter.value,
        page.value,
        per
      );
    });
    const generateSearchParamHash = (
      filter: ThreadFilterValue,
      nameOrUniqueId: string,
      unansweredOnly: boolean,
      page: number,
      per: number
    ) => {
      return SHA256(
        JSON.stringify({
          filter: convertThreadFilterValueToThreadFilter(filter),
          nameOrUniqueId: nameOrUniqueId,
          unansweredOnly: unansweredOnly,
          page: page,
          per: per
        })
      ).toString();
    };

    const handleChangedFilter = (value: ThreadFilterValue) => {
      currentFilter.value = value;
      saveToLocalStorage();
      resetInfiniteLoading();
    };

    const handleInputtedNameOrUniqueId = (value: string) => {
      nameOrUniqueIdFilter.value = value;
      saveToLocalStorage();
      resetInfiniteLoading();
    };

    const handleClickedSearchTextFieldClear = () => {
      nameOrUniqueIdFilter.value = "";
      saveToLocalStorage();
      resetInfiniteLoading();
    };

    const handleClickedUnansweredOnly = () => {
      unansweredOnlyFilter.value = !unansweredOnlyFilter.value;
      resetInfiniteLoading();
    };

    const saveToLocalStorage = () => {
      const data = {
        nameOrUniqueId: nameOrUniqueIdFilter.value,
        selection: currentFilter.value
      };
      localStorage.setItem("message:searchFilter", JSON.stringify(data));
    };

    const infiniteLoading = ref<InfiniteLoading | null>(null);

    const searchMessageThreadUsers = async () => {
      isLoadingMessageThreadUsers.value = true;

      const threadFilter = currentFilter.value;
      const nameOrUniqueId = nameOrUniqueIdFilter.value;
      const unansweredOnly = unansweredOnlyFilter.value;
      const currentPage = page.value;

      // 検索直前に検索パラメータを保持する
      lastSearchParam.value = generateSearchParamHash(
        threadFilter,
        nameOrUniqueId,
        unansweredOnly,
        currentPage,
        per
      );
      const responseItems = await fetchMessageThreads(
        convertThreadFilterValueToThreadFilter(threadFilter),
        nameOrUniqueId,
        unansweredOnly,
        currentPage,
        per
      ).finally(() => {
        isLoadingMessageThreadUsers.value = false;
      });

      return responseItems.map(item => {
        return convertSearchThreadsResponseItemToMessageThreadUser(item);
      });
    };

    const handleLoadedInfinite = async ($state: StateChanger) => {
      nextTick(async () => {
        // 検索パラメータが変わった時に際限なくリクエストが送信されてしまうため、それを抑制する
        if (isLoadingMessageThreadUsers.value) {
          return;
        }
        const loadedThreadUsers = await searchMessageThreadUsers();

        if (lastSearchParam.value !== currentSearchParamHash.value) {
          // 検索が完了した時に検索パラメータが既に変わっていた場合は検索結果を破棄してリセットする
          $state.loaded();
          resetInfiniteLoading();
        } else {
          if (loadedThreadUsers.length === 0) {
            $state.complete();
            return;
          }

          // ユーザーのプロフィール画像を追加
          const loadedThreadUsersWithImages = await Promise.all(
            loadedThreadUsers.map(async (user: MessageThreadUser) => {
              const imageUrl = await getImageDataUrl(user.userId);
              return { ...user, userImageUrl: imageUrl || DefaultUserImage };
            })
          );

          messageThreadUsers.value = messageThreadUsers.value.concat(
            loadedThreadUsersWithImages
          );

          page.value += 1;

          $state.loaded();
        }
      });
    };

    const resetInfiniteLoading = () => {
      page.value = 1;
      messageThreadUsers.value = [];
      infiniteLoading.value?.stateChanger.reset();
    };

    const handleClickedThreadUser = (threadUser: MessageThreadUser) => {
      emit("select-message-thread-user", threadUser);
    };

    const setReadCommentCount = (threadId: number) => {
      const user = messageThreadUsers.value.find(
        user => user.messageThreadId === threadId
      );
      if (!user) {
        return;
      }
      // NOTE: messageThreadUsersの値も更新される
      user.unreadCommentCount = 0;
    };

    const updateLastMessage = (updateThread: MessageThreadsLastComment) => {
      const thread = messageThreadUsers.value.find(
        thread => thread.messageThreadId === updateThread.messageThreadId
      );
      if (!thread) {
        return;
      }

      thread.lastMessageText = updateThread.lastMessageText;
      thread.lastMessageTimestampMs = updateThread.lastMessageTimestampMs;
    };

    const handleClickedScoutButton = () => {
      router.push({ name: "ScoutCandidates" });
    };

    return {
      occWhite,
      filterSelectList,
      currentFilter,
      nameOrUniqueIdFilter,
      unansweredOnlyFilter,
      messageThreadUsers,
      handleChangedFilter,
      handleInputtedNameOrUniqueId,
      handleClickedSearchTextFieldClear,
      handleClickedUnansweredOnly,
      infiniteLoading,
      handleLoadedInfinite,
      handleClickedThreadUser,
      handleClickedScoutButton
    };
  }
});
